import React, {useEffect, useState, useRef, memo} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {
  AllResultsHeader,
  TestResultHeader,
  TitleOnlyHeader,
  UserProfileHeader,
  HealthProfileHeader,
  HealthPlansHeader,
  BespokeHeader,
  HeaderWithTabs,
} from "./Variants";
import RenderIf from "@/components/Global/RenderIf/RenderIf";

import styles from "./Header.module.scss";
import HealthScoreHeader from "./Variants/HealthScoreHeader/HealthScoreHeader";

export default memo(function Header({
  variant = "titleOnlyHeader",
  children,
  className = "",
  handleBackFn,
  backButtonText = "",
  tabs = [],
  onTabClick = () => {},
  maxPlusTestInDays = 0,
  showFreeDeliveryItem = true,
  showTrend = false,
  partnerLogo = "",
  partnerRequestCode = "",
  ...rest
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [hasScrollDown, setHasScrollDown] = useState();
  const el = document.getElementById("root");

  useEffect(() => {
    if (el) {
      el.addEventListener("scroll", e => handleScrollEvent(e));
    }

    return () => {
      el && el.removeEventListener("scroll", e => handleScrollEvent(e));
    };
  }, [el]);

  const handleBack = () => {
    if (handleBackFn) handleBackFn();
    if (!handleBackFn) {
      if (location.key !== "default") {
        navigate(-1);
      } else {
        navigate("/");
      }
    }
  };

  const handleScrollEvent = e => {
    if (e.target.scrollTop >= 50) {
      setTimeout(() => setHasScrollDown(true), 50);
    }

    if (e.target.scrollTop < 20) {
      setTimeout(() => setHasScrollDown(false), 50);
    }
  };

  let customClassName = "";
  if (variant != "bespokeTestHeader") {
    customClassName = `${styles.header} ${hasScrollDown ? styles.shadow : styles.noShadow}`;
  }

  return (
    <>
      <header {...rest} className={`container-fluid g-0 ${customClassName} ${className}`}>
        <RenderIf isTrue={variant === "titleOnlyHeader"}>
          <TitleOnlyHeader handleBack={handleBack} backButtonText={backButtonText}>
            {children}
          </TitleOnlyHeader>
        </RenderIf>

        <RenderIf isTrue={variant === "userProfileHeader"}>
          <UserProfileHeader handleBack={handleBack}>{children}</UserProfileHeader>
        </RenderIf>

        <RenderIf isTrue={variant === "healthProfileHeader"}>
          <HealthProfileHeader handleBack={handleBack} backButtonText={backButtonText}>
            {children}
          </HealthProfileHeader>
        </RenderIf>

        <RenderIf isTrue={variant === "allResultsHeader"}>
          <AllResultsHeader />
        </RenderIf>

        <RenderIf isTrue={variant === "healthScoreHeader"}>
          <HealthScoreHeader handleBack={handleBack} backButtonText={backButtonText} showTrend={showTrend}>
            {children}
          </HealthScoreHeader>
        </RenderIf>

        <RenderIf isTrue={variant === "multipleBiomarkersHeader"}>
          <TestResultHeader
            handleBack={handleBack}
            showTabs={true}
            backButtonText={backButtonText}
            onTabClick={onTabClick}
          />
        </RenderIf>

        <RenderIf isTrue={["myFormHeader", "singleBiomarkerHeader", "antibodyHeader"].includes(variant)}>
          <TestResultHeader
            handleBack={handleBack}
            showExportButton={variant !== "antibodyHeader"}
            showTabs={false}
            isSingleBiomarkerHeader={variant === "singleBiomarkerHeader"}
            backButtonText={backButtonText}
          />
        </RenderIf>

        <RenderIf isTrue={variant === "healthPlansHeader"}>
          <HealthPlansHeader />
        </RenderIf>

        <RenderIf isTrue={variant === "bespokeTestHeader"}>
          <BespokeHeader
            maxPlusTestInDays={maxPlusTestInDays}
            showFreeDeliveryItem={showFreeDeliveryItem}
            partnerLogo={partnerLogo}
            partnerRequestCode={partnerRequestCode}
          />
        </RenderIf>

        <RenderIf isTrue={variant === "tabs"}>
          <HeaderWithTabs tabs={tabs}>{children}</HeaderWithTabs>
        </RenderIf>
      </header>
    </>
  );
});
